import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { CheckCircle2, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import api, { apiError } from "@/lib/api";

export default function RegisterBnb() {
  const navigate = useNavigate();
  const [cities, setCities] = useState([]);
  const [done, setDone] = useState(false);
  const [loading, setLoading] = useState(false);
  const [f, setF] = useState({
    name: "", referente: "", email: "", password: "", phone: "", address: "", city: "",
    cap: "", piva: "", rooms_count: 1, website: "", description: "",
    accept_privacy: false, accept_terms: false,
  });
  const set = (k, v) => setF({ ...f, [k]: v });

  useEffect(() => { api.get("/settings/public").then(({ data }) => setCities(data.cities)).catch(() => {}); }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!f.accept_privacy || !f.accept_terms) { toast.error("Devi accettare privacy e condizioni."); return; }
    setLoading(true);
    try {
      await api.post("/auth/register/bnb", { ...f, rooms_count: Number(f.rooms_count) });
      setDone(true);
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  if (done) return <SuccessBox onHome={() => navigate("/")} />;

  return (
    <div className="max-w-3xl mx-auto px-5 lg:px-8 py-12">
      <span className="text-xs uppercase tracking-[0.2em] font-semibold text-orange">Registrazione B&B</span>
      <h1 className="mt-3 text-3xl lg:text-4xl font-extrabold text-navy tracking-tight">Registra la tua struttura</h1>
      <p className="mt-3 text-navy/60">Compila i dati. Il team MorningBox controllerà le informazioni prima dell'attivazione.</p>
      <form onSubmit={submit} className="mt-8 bg-white border border-navy/10 rounded-3xl p-8 grid sm:grid-cols-2 gap-4" data-testid="register-bnb-form">
        <Field label="Nome struttura" v={f.name} on={(v) => set("name", v)} tid="bnb-name" required />
        <Field label="Nome referente" v={f.referente} on={(v) => set("referente", v)} tid="bnb-referente" required />
        <Field label="Email" type="email" v={f.email} on={(v) => set("email", v)} tid="bnb-email" required />
        <Field label="Password" type="password" v={f.password} on={(v) => set("password", v)} tid="bnb-password" required />
        <Field label="Telefono" v={f.phone} on={(v) => set("phone", v)} tid="bnb-phone" required />
        <Field label="Partita IVA / Codice fiscale" v={f.piva} on={(v) => set("piva", v)} tid="bnb-piva" required />
        <Field label="Indirizzo" v={f.address} on={(v) => set("address", v)} tid="bnb-address" required />
        <div>
          <Label>Città</Label>
          <Select value={f.city} onValueChange={(v) => set("city", v)}>
            <SelectTrigger className="rounded-xl mt-1 h-11" data-testid="bnb-city"><SelectValue placeholder="Seleziona città" /></SelectTrigger>
            <SelectContent>{cities.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <Field label="CAP" v={f.cap} on={(v) => set("cap", v)} tid="bnb-cap" required />
        <Field label="Numero camere" type="number" v={f.rooms_count} on={(v) => set("rooms_count", v)} tid="bnb-rooms" required />
        <Field label="Sito web (facoltativo)" v={f.website} on={(v) => set("website", v)} tid="bnb-website" />
        <div className="sm:col-span-2"><Label>Descrizione</Label><Textarea value={f.description} onChange={(e) => set("description", e.target.value)} className="rounded-xl mt-1" rows={3} data-testid="bnb-description" /></div>
        <div className="sm:col-span-2 space-y-2 pt-2">
          <label className="flex items-center gap-2 text-sm text-navy/70"><Checkbox checked={f.accept_privacy} onCheckedChange={(v) => set("accept_privacy", !!v)} data-testid="bnb-privacy" /> Accetto la Privacy Policy</label>
          <label className="flex items-center gap-2 text-sm text-navy/70"><Checkbox checked={f.accept_terms} onCheckedChange={(v) => set("accept_terms", !!v)} data-testid="bnb-terms" /> Accetto le condizioni del servizio</label>
        </div>
        <Button type="submit" disabled={loading} className="sm:col-span-2 rounded-full bg-navy text-white h-12" data-testid="bnb-submit">
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Invia registrazione"}
        </Button>
      </form>
    </div>
  );
}

function Field({ label, v, on, type = "text", tid, required }) {
  return (
    <div>
      <Label>{label}</Label>
      <Input type={type} value={v} required={required} onChange={(e) => on(e.target.value)} className="rounded-xl mt-1 h-11" data-testid={tid} />
    </div>
  );
}

function SuccessBox({ onHome }) {
  return (
    <div className="max-w-xl mx-auto px-5 py-24 text-center" data-testid="register-success">
      <div className="w-16 h-16 rounded-full bg-successbg flex items-center justify-center mx-auto"><CheckCircle2 className="w-8 h-8 text-successtext" /></div>
      <h1 className="mt-6 text-2xl font-bold text-navy">Registrazione ricevuta</h1>
      <p className="mt-3 text-navy/60 leading-relaxed">Il team MorningBox controllerà i dati e ti contatterà prima dell'attivazione. Riceverai una email di conferma.</p>
      <Button onClick={onHome} className="mt-8 rounded-full bg-navy text-white h-11 px-8">Torna alla home</Button>
    </div>
  );
}
